import {computed, getCurrentInstance} from 'vue'
import {i18n} from "store/global/locale";
import dayjs from "dayjs";
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core";
import {formatDateDE, formatDateTimeDE} from "utils/day";
import {DateRange, DateRangePresets} from "models/global/DateRange";
import {globalStore} from "store/global";
import isBetween from 'dayjs/plugin/isBetween'

dayjs.extend(isBetween)

export const capitalize = (value) => value[0].toUpperCase() + value.substr(1)
export const lowercase = (value) => value[0].toLowerCase() + value.substr(1)

export const formatValue = (value: number, type: string | "string" | "none" | "number" | "integer" | "date" | "datetime" | "currency" | "percentage" | "temperature" = "none", currency?: string, options: any = {
	decimalDigits: 2
}): string => {

	if (!value && value !== 0) {
		return;
	}

	if(options === null){
		options = {
			decimalDigits: 2
		};
	}

	if(type === "percentage" && options.maximumFractionDigits == null){
		options.maximumFractionDigits = 2;
	}

	if (type === "string") {
		return value.toString();
	} else if (type === "number") {
		return formatNumber(value, currency, options)
	} else if (type === "integer") {
		return Math.round(Number(value)).toString()
	}  else if (type === "date") {
		return formatDateDE(value);
	}  else if (type === "datetime") {
		return formatDateTimeDE(value);
	} else if (type === "currency") {
		return formatPrice(value, currency, options)
	} else if (type === "percentage") {
		return new Intl.NumberFormat(navigator.language, options).format(value) + "%"
	} else if (type === "temperature") {
		return new Intl.NumberFormat(navigator.language).format(value) + "°"
	} else if (type !== "none" && type !== "") {
		return new Intl.NumberFormat(navigator.language, options).format(value) + " " + i18n.value.global.t(type, value);
	}

	return value.toLocaleString(navigator.language);
}

export const formatNumber = (value: number, currency: string, options = {
	decimalDigits: 2,
	minimumFractionDigits: 0
}): string => {

	let decimalDigits = 2;
	let minimumFractionDigits = 0;

	if(options?.decimalDigits){
		decimalDigits = options.decimalDigits
	}
	if(options?.minimumFractionDigits){
		minimumFractionDigits = options.minimumFractionDigits
	}

	const customFormat = new Intl.NumberFormat(navigator.language, {
		minimumFractionDigits: minimumFractionDigits
	});

	value = Number(Number(value).toFixed(decimalDigits))
	return customFormat.format(value)

}

export const formatPrice = (value: number, currency: string, options = {
	decimalDigits: 2,
	minimumFractionDigits: 0
}): string => {

	const formattedValue = formatNumber(value, currency, options)
	if (currency === "EUR") {
		return formattedValue + " €"
	} else if (currency === "CHF") {
		return formattedValue + " Fr."
	} else if (currency === "USD") {
		return formattedValue + " $"
	} else if (currency === "GBP") {
		return formattedValue + " £"
	} else {
		return formattedValue + " " + currency;
	}
}

export const formatPriceWithDecimals = (value: number) => {
	const useGlobalStore = globalStore()
	return formatValue(value, 'currency', useGlobalStore?.currentHotel?.currency, {minimumFractionDigits: 2})
}

export const roundValue = (value: string | number, decimals: number = 2): number => {

	if(typeof value === "string"){
		return Number(Number(value).toFixed(decimals))
	}

	return Number(value.toFixed(decimals))

}

export const useVModel = (props, propName, format: (any) => void | null = null) => {
	const vm = getCurrentInstance().proxy

	return computed({
		get() {
			if (format) {
				return format(props[propName])
			}
			return props[propName]
		},
		set(value) {
			if (format) {
				vm.$emit(`update:${propName}`, format(value))
			} else {
				vm.$emit(`update:${propName}`, value)
			}
		},
	})
}

export const hslToHex = (h, s, l) => {
	l /= 100;
	const a = s * Math.min(l, 1 - l) / 100;
	const f = n => {
		const k = (n + h / 30) % 12;
		const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
		return Math.round(255 * color).toString(16).padStart(2, '0');   // convert to Hex and prefix "0" if needed
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

export const stringToColour = (str: string) => {
	let hash = 0;
	// reversing string to bring more variation
	str = str.split("").reverse().join("");
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return 'hsl(' + hash % 360 + ', 80%, 60%)';
}

export const numberToColour = (number: number) => {
	return hslToHex(number % 360, 80, 60);
}

export const generateColorPalette = () => {
	const colorPalette = ["#006CD1", "#D9392E", "#FFBE00", "#188743", "#E427E8", "#272D4B", "#404048", "#F09EA0", "#000000", "#00FF00", "#0000FF", "#FF0000", "#01FFFE", "#FFA6FE", "#FFDB66", "#006401", "#010067", "#95003A", "#007DB5", "#FF00F6", "#FFEEE8", "#774D00", "#90FB92", "#0076FF", "#D5FF00", "#FF937E", "#6A826C", "#FF029D", "#FE8900", "#7A4782", "#7E2DD2", "#85A900", "#FF0056", "#A42400", "#00AE7E", "#683D3B", "#BDC6FF", "#263400", "#BDD393", "#00B917", "#9E008E", "#001544", "#C28C9F", "#FF74A3", "#01D0FF", "#004754", "#E56FFE", "#788231", "#0E4CA1", "#91D0CB", "#BE9970", "#968AE8", "#BB8800", "#43002C", "#DEFF74", "#00FFC6", "#FFE502", "#620E00", "#008F9C", "#98FF52", "#7544B1", "#B500FF", "#00FF78", "#FF6E41", "#005F39", "#6B6882", "#5FAD4E", "#A75740", "#A5FFD2", "#FFB167", "#009BFF", "#E85EBE"];
	return colorPalette;
}


export const getMinMax = (values1: number[], values2: number[], roundUp: number | boolean = true) => {
	let all = [...values1, ...values2];

	all = all.filter(value => !!value);
	all = all.map(value => Number(value.toFixed(0)));

	if (roundUp === true) {
		roundUp = Math.pow(10, (Math.max.apply(Math, all)).toString().length - 1);
	} else {
		roundUp = 1;
	}

	return {
		min: Math.min.apply(Math, all),
		max: roundUpNearest(Math.max.apply(Math, all), roundUp),
	}
}

export const roundUpNearest = (num: number, nearest = 10) => {
	return Number.isFinite(Math.ceil(num / nearest) * nearest) ? Math.ceil(num / nearest) * nearest : 0;
}

export const safeDivide = (x: number, y: number):number => {
	if(x === 0 || y === 0 || x === undefined || y === undefined){
		return 0
	}

	return x /y;
}

export const arrayToSummarizedString = (items = [], maxNumberOfItems = 3, itemLabel = "", t) => {
	let itemString = items.slice(0, maxNumberOfItems).join(", ");

	if(items.length > maxNumberOfItems){
		itemString += `, +${items.length - maxNumberOfItems} ${t(itemLabel, items.length - maxNumberOfItems)}`
	}

	return itemString;
}

export const datePresetToDateRange = (value, dateFormat = "YYYY-MM-DD"): DateRange => {

	const presets: any = Object.values(DateRangePresets);
	const dateRange = new DateRange();
	dateRange.preset = value

	if (presets.includes(value)) {

		switch (value) {
			case DateRangePresets.CURRENT_MONTH:
				dateRange.from = dayjs().startOf("month").format(dateFormat);
				dateRange.to = dayjs().subtract(1, 'day').format(dateFormat);
				break;
			case DateRangePresets.CURRENT_FULL_MONTH:
				dateRange.from = dayjs().startOf("month").format(dateFormat);
				dateRange.to = dayjs().endOf("month").format(dateFormat);
				break;
			case DateRangePresets.NEXT_FULL_MONTH:
				dateRange.from = dayjs().add(1, 'month').startOf("month").format(dateFormat);
				dateRange.to = dayjs().add(1, 'month').endOf("month").format(dateFormat);
				break;
			case DateRangePresets.PREVIOUS_FULL_MONTH:
				dateRange.from = dayjs().subtract(1, 'month').startOf("month").format(dateFormat);
				dateRange.to = dayjs().subtract(1, 'month').endOf("month").format(dateFormat);
				break;
			default:
				console.warn("Invalid preset, or it hasn't been implemented yet");
				return;
		}

		return dateRange
	}

	if (value < 0) {
		dateRange.from = dayjs().subtract(Math.abs(value), "days").format(dateFormat);
		dateRange.to = dayjs().subtract(1, "day").format(dateFormat);
	} else {
		dateRange.from = dayjs().format(dateFormat);
		dateRange.to = dayjs().add(Math.abs(value), "days").format(dateFormat);
	}

	return dateRange;

}

export const getDateRangeLabel = (value:DateRange, t, showFromToOnStatic: boolean = false) => {

	if(!value){
		return "";
	}

	if(!value.preset && (value.from && value.to)){
		return showFromToOnStatic ? formatDateDE(value.from)+" - "+formatDateDE(value.to) : t(`GLOBAL.DATEPICKER_STATIC_DATE`)
	}

	const regInt = /^-?\d+$/;
	//@ts-ignore
	if(regInt.test( value.preset )){
		return value.preset +' '+ t('DASHBOARD.DAYS_PLURAL', Number(value.preset))
	}else{
		const translationKey = Object.keys(DateRangePresets)[Object.values(DateRangePresets).findIndex(localValue => localValue === value.preset)];
		if(translationKey){
			return t(`GLOBAL.DATEPICKER_${translationKey}`)
		}
	}

	return ""
}

export const isMac = () => {
	return navigator.platform.indexOf('Mac') > -1
}

export const isWindows = () => {
	return navigator.platform.indexOf('Win') > -1
}

export const getWelcomeMessage = (t) => {
	return computed(() => {

		const currentDate = dayjs();
		const currentHour = currentDate.hour();

		if (currentDate.isBetween(dayjs().month(11).date(22).hour(0).minute(0), dayjs().month(11).date(26).hour(23).minute(59))) {
			return {message: t("LOGIN.WELCOME_HAPPY_HOLIDAYS"), icon: 'sleigh'};
		}

		if (currentDate.isBetween(dayjs().month(11).date(30).hour(0).minute(0), dayjs().month(12).date(2).hour(23).minute(59))) {
			return {message: t("LOGIN.WELCOME_HAPPY_NEW_YEAR"), icon: 'champagne-glasses'};
		}

		if (currentHour >= 3 && currentHour <= 12) {
			return {message: t("LOGIN.WELCOME_MORNING")};
		}

		if (currentHour >= 12 && currentHour <= 18) {
			return {message: t("LOGIN.WELCOME_DAY")};
		}

		return {message: t("LOGIN.WELCOME_EVENING")};

	})
}

export const breakpoints = useBreakpoints({...breakpointsTailwind, ...{'3xl':1920}})
export const isMobile = breakpoints.smaller("lg").value

export const isTouch = () => {
	// @ts-ignore
	return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)
};

export const getDecimalSeparator = (locale):string => {
	try{
		const numberWithDecimalSeparator = 1.1;
		return Intl.NumberFormat(locale)
			.formatToParts(numberWithDecimalSeparator)
			.find(part => part.type === 'decimal')
			.value;
	}catch (e) {
		console.warn(e)
		return "."
	}

}

export const translatedTextFrontendHelper = (t, value, currency) => {
	if(Array.isArray(value?.values)){
		let processedValues = value.values.map((_value) => {

			// Price Log returns values in map object
			if(_value.map){
				_value = _value.map
			}

			return formatValue(_value.value, _value.type, currency)
		})

		return t(value.key ?? value.translationKey, processedValues)
	}else if (value.key && typeof value?.values === 'object'){
		let formattedValues = {};

		Object.keys(value?.values).forEach((_value) => {
			formattedValues[_value] = formatValue(value?.values[_value].value, value?.values[_value].type, currency)
		})

		console.log(value.key, formattedValues)
		return t(value.key, formattedValues)
	}else if (value.key){
		return t(value.key)
	}else if (value.key && typeof value?.values === 'object'){
		let formattedValues = {};

		Object.keys(value?.values).forEach((_value) => {
			formattedValues[_value] = formatValue(value?.values[_value].value, value?.values[_value].type, currency)
		})

		return t(value.key, formattedValues)
	}else if (value.key){
		return t(value.key)
	}else{
		return "";
	}
}

export const StringToBoolean = (string) =>
	string === 'false' || string === 'undefined' || string === 'null' || string === '0' ?
		false : !!string


export const formatCurrency = (currency: string): string => {
	if (currency === "EUR") {
		return "€"
	} else if (currency === "CHF") {
		return "Fr."
	} else if (currency === "USD") {
		return "$"
	} else if (currency === "GBP") {
		return "£"
	}

	return currency;
}